import type { FetchResult, Reference, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { parseArgs } from '@utils/apollo';

import {
  ListDocumentsDocument,
  useListDocumentsQuery,
  useSetDocumentsUploadedMutation,
  useUpdateDocumentRecordMutation,
  useUploadClientRecordsMutation,
  useUploadImportJobCsvMutation,
} from 'generated/document.generated';
import type {
  SetDocumentsUploadedMutation,
  SetDocumentsUploadedMutationVariables,
  UpdateDocumentRecordMutation,
  UpdateDocumentRecordMutationVariables,
  UploadClientRecordsMutation,
  UploadClientRecordsMutationVariables,
  UploadImportJobCsvMutation,
  UploadImportJobCsvMutationVariables,
} from 'generated/document.generated';

export const useGetDocuments = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id;
  const response = useListDocumentsQuery({
    variables: {
      input: { vitall_patient_id: vitall_patient_id ?? '' },
    },
    skip: !vitall_patient_id,
  });
  handleError(response.error);
  return {
    ...response,
    data: response.data?.listDocuments,
  };
};

export function useUploadClientRecords(): [
  (
    params: UploadClientRecordsMutationVariables
  ) => Promise<FetchResult<UploadClientRecordsMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUploadClientRecordsMutation();
  const loading = baseLoading;

  async function uploadClientRecords(
    params: UploadClientRecordsMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [uploadClientRecords, loading];
}

export function useUploadImportJobCsv(): [
  (
    params: UploadImportJobCsvMutationVariables
  ) => Promise<FetchResult<UploadImportJobCsvMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUploadImportJobCsvMutation();
  const loading = baseLoading;

  async function uploadCsv(params: UploadImportJobCsvMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [uploadCsv, loading];
}

export function useSetDocumentsUploaded(): [
  (
    params: SetDocumentsUploadedMutationVariables
  ) => Promise<FetchResult<SetDocumentsUploadedMutation>>,
  boolean,
] {
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id;
  const [mutate, { loading: baseLoading }] = useSetDocumentsUploadedMutation();
  const loading = baseLoading;

  async function setDocumentsUploaded(
    params: SetDocumentsUploadedMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.setDocumentsUploaded) {
          return;
        }
        const storeObjects = response.setDocumentsUploaded.map(
          (document) => document as unknown as StoreObject
        );
        try {
          cache.modify<{ listDocuments: Reference[] }>({
            fields: {
              listDocuments: (existing, { storeFieldName, toReference }) => {
                const parsed = parseArgs(storeFieldName);
                if (vitall_patient_id === parsed?.vitall_patient_id) {
                  return [
                    ...existing,
                    ...storeObjects
                      .map((storeObject) => toReference(storeObject))
                      .filter((ref) => !!ref),
                  ];
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [setDocumentsUploaded, loading];
}

export function useUpdateDocument(): [
  (
    params: UpdateDocumentRecordMutationVariables
  ) => Promise<FetchResult<UpdateDocumentRecordMutation>>,
  boolean,
] {
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id;
  const [mutate, { loading: baseLoading }] = useUpdateDocumentRecordMutation();
  const loading = baseLoading;

  async function updateDocument(params: UpdateDocumentRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      refetchQueries: [
        {
          query: ListDocumentsDocument,
          variables: {
            vitall_patient_id,
          },
        },
      ],
    });
    return response;
  }
  return [updateDocument, loading];
}
